import { Button } from '@ophelos/ui/components/button';
import { ArrowRight } from 'lucide-react';
import { useAIPanel } from '@/contexts/AIContext';
import { useDebtData } from '@/contexts/DebtDataContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { MOBILE_QUERY, useMediaQuery } from '@/hooks/useMediaQuery';
import { usePreviewNavigate } from '@/hooks/usePreviewNavigate';
import { useTranslation } from '@/hooks/useTranslation';
import { formatMoneyInLanguage } from '@/lib/currency';
import { debtHeadline, debtPrice } from '@/lib/debtHeadline';
import { type PortalLinkState, type PortalPageRequest, portalPageLink } from '@/lib/portalNavigation';
import { scheduledPaymentAmount } from '@/lib/paymentPlans';
import { type PaymentTab, TAB_DISCOUNT } from '@/lib/paymentTabs';
import type { DebtData } from '@/types';

/**
 * The buttons to the pages the agent offered with a reply, one per page. Resolved on every
 * render from the portal's current state, so they wait for it to load, follow a debt that has
 * since been cleared, and drop a page that no longer has anything to offer. A debt's button
 * names it by reference and the amount the page it opens asks for: the payment already scheduled,
 * else its payment tab's price, or elsewhere the amount its dashboard card shows.
 */
export function PortalPageButtons({ requests, linkState }: { requests: PortalPageRequest[]; linkState: PortalLinkState | null }) {
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();
  const { activeOffers } = useDebtData();
  const { closePanel } = useAIPanel();
  const navigate = usePreviewNavigate();
  const isMobile = useMediaQuery(MOBILE_QUERY);
  const quote = (debt: DebtData, tab: PaymentTab | undefined) =>
    scheduledPaymentAmount(debt.paymentPlan) ??
    (tab ? debtPrice(debt, activeOffers[debt.id], TAB_DISCOUNT[tab]) : debtHeadline(debt, activeOffers[debt.id])).amount;

  const resolved = requests.flatMap((request) => portalPageLink(request, linkState) ?? []);
  const links = [...new Map(resolved.map((link) => [link.to, link])).values()];
  if (!links.length) return null;

  const open = (to: string) => {
    navigate(to);
    // The phone overlay covers the page; the conversation is kept for when it reopens.
    if (isMobile) closePanel();
  };

  return (
    <div className="mt-2 flex flex-wrap gap-2">
      {links.map(({ to, kind, debt, tab }) => (
        <Button key={to} variant="outline" size="sm" className="rounded-full shadow-none border-border-heavy" onClick={() => open(to)}>
          {t(`aiHelper.openPage.${kind}`)}
          {debt && (
            <span className="text-muted-foreground">
              {debt.externalCaseRef} ({formatMoneyInLanguage(quote(debt, tab), currentLanguage, debt.currency)})
            </span>
          )}
          <ArrowRight className="size-3.5" />
        </Button>
      ))}
    </div>
  );
}
